/**
 * Typed client for the BackDoor API Gateway (https://api.backdoor.tech).
 * Schemas mirror openapi.fresh.json. Reads the access token from localStorage
 * and attaches it as `Authorization: Bearer ...`.
 */

import { translate } from "./i18n";
import { clearSessionFlag, markSessionActive } from "./sessionFlag";
import { demoReportMatch, demoStore, handleDemoRequest, inDemoScope } from "./demo/demoApi";
import { demoReportHtml } from "./demo/demoReport";

const TOKEN_KEY = "ces.token";
const REFRESH_KEY = "ces.refresh_token";
const USER_KEY = "ces.user";
export const AUTH_CHANGED_EVENT = "ces.auth.changed";

function emitAuthChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  }
}

// ---------- Types: auth ----------

export type User = {
  user_id: string;
  owner_user_id: string;
  email: string;
  display_name: string;
  email_verified?: boolean;
};

export type AuthResponse = {
  access_token: string;
  refresh_token: string;
  token_type?: string;
  expires_in: number;
  auth_method: string;
  user: User;
};

// Spec: IdentityProviderInfo — provider_key, display_name, login_url.
export type SsoProvider = {
  provider_key: string;
  display_name: string;
  login_url: string;
};

export type SsoStartResponse = {
  provider: SsoProvider;
  redirect_url: string;
};

// ---------- Types: projects + scope ----------

export type Project = {
  project_id: string;
  name: string;
  description: string;
  owner_user_id?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  total_scope_targets?: number;
  verified_targets?: number;
  // Jurisdiction drives the report's "Legal & regulatory risks" section. The
  // name rides along with the code so the project list needn't resolve it via a
  // second call to the catalog; it is populated even for retired jurisdictions.
  jurisdiction_code?: string;
  jurisdiction_name?: string;
};

// A single entry from `GET /api/v1/jurisdictions`. The user-facing surface
// deliberately carries only code + English name — `is_default` and the LLM
// directive live on a separate admin-only model and never reach the client.
export type Jurisdiction = {
  code: string;
  name: string;
};

// Spec: ScopeTargetInfo. Replaces the old ScopeDomain — a target is now either a
// domain or an infra asset (`kind: "ip" | "cidr"`, which carries an `attestation`
// of authorization). The domain-only fields (`domain`/`domain_id`) were renamed to
// `target`/`target_id`.
export type ScopeTargetKind = "domain" | "ip" | "cidr" | (string & {});

export type ScopeTarget = {
  target_id: string;
  project_id: string;
  target: string;
  kind: ScopeTargetKind;
  attestation: string | null;
  verification_status: "pending" | "verified" | "expired" | "failed" | string;
  verification_method: string | null;
  verification_token: string | null;
  verification_instruction: string | null;
  verification_started_at: string | null;
  verified_at: string | null;
  verification_expires_at: string | null;
  failure_reason: string | null;
  created_at: string;
};

export type VerificationCompleteResponse = {
  target: ScopeTarget;
  verified: boolean;
  failure_reason: string | null;
};

export type DeleteResponse = {
  deleted: boolean;
  mode?: string;
  id?: string | null;
  deleted_at?: string | null;
};

export type CursorParams = {
  cursor?: string | null;
  page_size?: number | null;
};

// ---------- Types: scans ----------
// Spec: ScanInfo / ScanListItem — no `phase` or `progress`; progress is
// expressed as `progress_percent` (0..100) and the active step is identified
// by `current_node_id` within `pipeline_name`.

export type Scan = {
  scan_id: string;
  project_id: string;
  status: "running" | "paused" | "cancelled" | "completed" | "failed" | string;
  pipeline_name?: string | null;
  pipeline_version?: string | null;
  current_node_id?: string | null;
  progress_percent?: number | null;
  nodes_total?: number | null;
  nodes_done?: number | null;
  findings_count: number;
  critical_count: number;
  high_count: number;
  error_message?: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
};

export type ScanListItem = {
  scan_id: string;
  target_id?: string | null;
  project_id?: string | null;
  owner_user_id?: string | null;
  status: string;
  pipeline_name?: string | null;
  pipeline_version?: string | null;
  current_node_id?: string | null;
  progress_percent?: number | null;
  nodes_total?: number | null;
  nodes_done?: number | null;
  findings_count: number;
  critical_count?: number | null;
  high_count?: number | null;
  created_at: string | null;
  started_at?: string | null;
  completed_at?: string | null;
};

export type StartScanResponse = {
  scan_id: string;
  status: string;
  target_id: string;
  project_id: string;
  pipeline_name?: string;
  pipeline_version?: string;
};

// Spec (planning#213): ReportVerificationInfo, GET /scans/{id}/report/verification.
// Manual attestation of the report's *composition* by a pentester. Three axes
// that must not be conflated: the technical finding status, the per-finding
// manual decision, and this report-level act. The only field that may drive a
// "Human validated" mark in the UI is `is_verified`; `verified_by` /
// `verified_at` stay populated as history after the act goes stale or is
// revoked. Before the first finalization the server answers 200 with
// `not_completed`, not 404. Enums keep `| string` for forward compatibility.
export type ReportVerificationCounts = {
  total: number;
  confirmed: number;
  false_positive: number;
  unreviewed: number;
};

export type ReportVerificationSnapshotCounts = {
  total: number;
  included: number;
  excluded_unreviewed: number;
  excluded_false_positive: number;
};

export type ReportVerificationInfo = {
  scan_id: string;
  manual_verification_state: "not_completed" | "verified" | "stale" | "revoked" | string;
  finding_selection_mode: "automated" | "manual_confirmed" | string;
  status: "unverified" | "verified" | "stale" | string;
  is_verified: boolean;
  verification_id: string | null;
  review_revision: number;
  verified_revision: number | null;
  verified_by: { user_id: string; display_name: string } | null;
  verified_at: string | null;
  snapshot_counts: ReportVerificationSnapshotCounts | null;
  current_counts: ReportVerificationCounts;
  strict_mode: boolean;
  can_finalize: boolean;
};

// Spec: FindingDetail (GET /scans/{id}/findings/{id}). List items come back as
// `additionalProperties: true` objects with the same shape in practice.
// Legacy fields (kind/status/progress/score/url/evidence/cvss/poc) are kept
// optional for tolerance with older payloads.
export type Finding = {
  finding_id: string;
  scan_id: string;
  target_id?: string;
  title: string;
  description?: string;
  severity: "critical" | "high" | "medium" | "low" | "info" | string;
  vuln_type?: string;
  target?: string;
  target_url?: string;
  target_host?: string;
  tool_name?: string;
  is_verified?: boolean;
  is_false_positive?: boolean;
  remediation?: string;
  created_at?: string | null;
  updated_at?: string | null;
  // Legacy / opaque fallbacks
  kind?: string;
  status?: string;
  progress?: number;
  score?: number;
  poc?: string | null;
  url?: string | null;
  evidence?: string | null;
  cvss?: number | string | null;
};

// Attack vectors = what the LLM proposed to test during analysis/agent_loop —
// the "hypotheses" shown on the Hypotheses page (distinct from confirmed
// Findings). Lifecycle status: pending → assigned → tested →
// tested_no_finding | confirmed | failed.
//
// The dedicated `GET /scans/{id}/vectors` endpoint was REMOVED from the backend;
// vectors are now emitted into the scan events feed (`/scans/{id}/events`) and
// reconstructed client-side by `deriveAttackVectorsFromEvents` (see below).
// This is the UI model those events are mapped onto — stay tolerant of extra
// fields, backend payloads are additionalProperties:true.
export type AttackVectorStatus =
  | "pending"
  | "assigned"
  | "tested"
  | "tested_no_finding"
  | "confirmed"
  | "failed"
  | (string & {});

export type AttackVector = {
  vector_id: string;
  scan_id: string;
  vuln_type: string;
  target?: string;
  host: string;
  severity?: string;
  reason?: string;
  how_to_test?: string;
  status: AttackVectorStatus;
  created_at?: string | null;
};

export type ScanEvent = {
  event_id: string;
  scan_id: string;
  event_type: string;
  sequence_number: string;
  phase?: string;
  node_path?: string;
  node_type?: string;
  message: string;
  payload?: Record<string, unknown>;
  timestamp: string;
};

export type ListScansParams = CursorParams & {
  project_id?: string | null;
  target_id?: string | null;
};

// ---------- Narration (Cloudflare Worker, see /worker) ----------
//
// The live chat feed turns raw scan events into punchy manager-facing bubbles.
// The rewrite runs in a Cloudflare Worker (`worker/`) so the DeepSeek key stays
// server-side, never in the browser bundle. The URL is injected at build time
// via `VITE_NARRATE_URL`; when it's unset the ChatPanel falls back to the
// deterministic template layer (`fallbackBubble`) and never calls the worker.
export type NarrateItem = {
  id: string;
  /** finding | reasoning | tool | milestone */
  kind?: string;
  text?: string;
  severity?: string;
  target?: string;
  vuln_type?: string;
};

export type NarratedBubble = {
  id: string;
  emoji: string;
  title: string;
  body: string;
};

// Resolution order:
//   1. explicit VITE_NARRATE_URL (dev against `wrangler dev`, or a custom host);
//   2. otherwise in a production build → same-origin "/narrate" (the Worker is
//      bound to the route app.backdoor.tech/narrate*, so no env file is needed
//      and no CSP change — it's 'self'). Before the route exists the call fails
//      and ChatPanel falls back to the local template layer.
//   3. dev with nothing set → disabled.
const NARRATE_EXPLICIT = (import.meta.env.VITE_NARRATE_URL as string | undefined)?.trim();
export const NARRATE_URL: string | undefined = NARRATE_EXPLICIT
  ? NARRATE_EXPLICIT.replace(/\/$/, "")
  : import.meta.env.PROD
    ? "/narrate"
    : undefined;

export function isNarrationEnabled(): boolean {
  return Boolean(NARRATE_URL);
}

/**
 * Pipeline DAG node from /api/v1/scans/{id}/graph. Children are nested
 * recursively (mission → iter_NN → thinking + tool_MM_<name>).
 */
export type ScanGraphNode = {
  id: string;
  type: string;
  label: string;
  status: string;
  duration_ms: number;
  started_at: string | null;
  finished_at: string | null;
  error_message: string;
  iter_total: number;
  iter_done: number;
  is_compound: boolean;
  event_count: number;
  children: ScanGraphNode[];
};

export type ScanEventsParams = {
  cursor?: string | null;
  page_size?: number | null;
  level_min?: string | null;
  /** Comma-separated event-kind filter, e.g. "llm.response_received". */
  kinds?: string | null;
  /** Return the latest page_size events (newest) instead of from the cursor —
   *  for the initial load of a long scan. */
  tail?: boolean | null;
};

// ---------- Types: notifications ----------

export type NotificationContent = {
  title: string;
  body: string;
  short_body?: string;
  data?: Record<string, unknown>;
  action_url?: string;
  action_label?: string;
};

export type NotificationContext = {
  scan_id?: string;
  project_id?: string;
  finding_id?: string;
  report_id?: string;
  interrupt_id?: string;
  directive_id?: string;
};

export type Notification = {
  notification_id: string;
  user_id: string;
  event_type: string;
  content: NotificationContent;
  context: NotificationContext;
  channels: string[];
  delivery_status: string;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
};

export type NotificationPrefs = {
  user_id: string;
  event_preferences: Record<string, NotificationChannel[]>;
  global_mute: boolean;
  notification_email: string;
};

export type NotificationChannel = "websocket" | "email" | "push" | "webhook";

export type ListNotificationsParams = {
  only_unread?: boolean;
  page_size?: number;
  page_token?: string;
};

// ---------- Types: billing ----------
// Spec slimmed down: no `plan_limits`, no `allowed_scan_types`, no LLM/VM
// counters in `current_usage`.

/** One manually-set price for a plan, in a single currency, plus the payment
 *  providers that serve that currency (e.g. RUB → ["tbank","yookassa"]). The
 *  chosen provider is what `checkout` sends; the backend resolves the currency
 *  from it. Amount is in major units (e.g. 49.0, 3900.0). */
export type BillingPrice = { currency: string; amount: number; providers: string[] };

export type BillingPlan = {
  plan_tier: string;
  display_name?: string;
  description?: string;
  prices: BillingPrice[];
  max_scans_per_month: number;
  sort_order?: number;
  is_active?: boolean;
};

export type BillingSubscriptionDetails = {
  subscription_id: string;
  plan_tier: string;
  status: string;
  provider_subscription_id: string;
  current_period_start: string;
  current_period_end: string;
};

export type BillingUsage = {
  scans_this_month: number;
  additional_scans_credit?: number;
};

export type SubscriptionInfo = {
  subscription: BillingSubscriptionDetails;
  plan?: BillingPlan | null;
  current_usage: BillingUsage;
};

export type CheckoutResponse = {
  checkout_id: string;
  payment_id: string;
  invoice_id: string;
  provider: string;
  kind: string;
  plan_tier?: string;
  scans_count?: number;
  amount: number;
  currency: string;
  checkout_url: string;
  status: string;
  created_at: string;
  expires_at: string | null;
};

/** Per-unit price of an extra-scans pack, one entry per currency. Mirrors
 *  BillingPrice but the amount field is `price`. */
export type BillingScanPackPrice = { currency: string; price: number; providers: string[] };

export type BillingScanPackPricing = {
  prices: BillingScanPackPrice[];
};

export type BillingInvoice = {
  invoice_id: string;
  subscription_id: string;
  period_start: string;
  period_end: string;
  /** Charged amount in major units, in the actual settlement currency. */
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  download_url: string;
};

export type ApiKeyInfo = {
  api_key_id: string;
  name: string;
  prefix: string;
  scopes: string[];
  allowed_project_ids: string[];
  created_at: string;
  expires_at?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
};

export type ApiKeyCreateRequest = {
  name: string;
  scopes?: string[];
  allowed_project_ids?: string[];
  expires_in_days?: number | null;
};

export type ApiKeyCreateResponse = {
  api_key: ApiKeyInfo;
  secret: string;
};

// ---------- Pagination envelope ----------

type Cursored<K extends string, T> = {
  next_cursor?: string | null;
  has_more?: boolean;
  count?: number;
} & Record<K, T[]>;

// ---------- Auth storage ----------

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setAuth(resp: AuthResponse): void {
  localStorage.setItem(TOKEN_KEY, resp.access_token);
  localStorage.setItem(REFRESH_KEY, resp.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(resp.user));
  // Флаг для лендинга (кука на .backdoor.tech) — см. sessionFlag.ts.
  markSessionActive();
  emitAuthChanged();
}

export function setTokens(tokens: { access_token: string; refresh_token: string }): void {
  localStorage.setItem(TOKEN_KEY, tokens.access_token);
  localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
  markSessionActive();
  emitAuthChanged();
}

export function setStoredUser(user: User): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  // Вызывается после успешного /auth/me — сессия подтверждена сервером.
  // Так флаг появится и у тех, кто вошёл до появления куки, при первом
  // открытии приложения, а не только после логина/refresh.
  markSessionActive();
  emitAuthChanged();
}

export function getStoredUser(): User | null {
  const raw = localStorage.getItem(USER_KEY);
  try {
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  clearSessionFlag();
  emitAuthChanged();
}

// ---------- HTTP plumbing ----------

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "ApiError";
  }
}

type RequestOptions = {
  auth?: boolean;
  skipRefresh?: boolean;
};

type QueryValue = string | number | boolean | null | undefined;

function qs(params: object = {}): string {
  const search = new URLSearchParams();
  Object.entries(params as Record<string, QueryValue>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : "";
}

function authHeaders(includeJson: boolean, auth: boolean): Record<string, string> {
  const headers: Record<string, string> = {};
  if (includeJson) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

type PydanticIssue = {
  type?: string;
  loc?: unknown[];
  msg?: string;
  ctx?: Record<string, unknown>;
};

function formatPydanticIssue(issue: PydanticIssue): string {
  const loc = Array.isArray(issue.loc) ? issue.loc : [];
  const field = loc.slice(1).filter((p) => typeof p === "string" || typeof p === "number").join(".");
  const msg = typeof issue.msg === "string" && issue.msg ? issue.msg : "Invalid value";
  return field ? `${field}: ${msg}` : msg;
}

async function parseApiError(res: Response): Promise<ApiError> {
  let detail = res.statusText;
  let code: string | undefined;
  try {
    const data = await res.json();
    if (typeof data?.detail === "string") {
      detail = data.detail;
    } else if (Array.isArray(data?.detail)) {
      const messages = (data.detail as PydanticIssue[])
        .map(formatPydanticIssue)
        .filter(Boolean);
      detail = messages.length ? messages.join("; ") : res.statusText;
    } else if (typeof data?.message === "string") {
      detail = data.message;
    } else if (data && typeof data === "object") {
      detail = res.statusText;
    }
    code = typeof data?.code === "string" ? data.code : undefined;
  } catch {
    /* gateway 5xx often returns HTML or empty body — keep going */
  }
  // Gateway errors usually have no useful detail; surface a hint instead of
  // the bare "Bad Gateway" / "Service Unavailable".
  if (TRANSIENT_STATUSES.has(res.status) && (!detail || detail === res.statusText)) {
    detail = translate("errors.serverUnavailable", { status: res.status });
  }
  return new ApiError(res.status, detail, code);
}

// Single-flight refresh. During a scan several pollers run at once, so when the
// access token expires they all get 401 in the same tick. Without dedup each one
// fires its own POST /auth/refresh — which rate-limits the refresh endpoint (429)
// and, because the backend rotates the refresh token, races: the first refresh
// wins and rotates the token, the losers send the now-stale token, get 401, and
// clearAuth() logs the user out mid-scan. Collapsing concurrent refreshes into a
// single shared promise means only one request ever hits /auth/refresh.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAuthSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refresh_token = getRefreshToken();
    if (!refresh_token) return false;
    try {
      const refreshed = await request<AuthResponse>(
        "POST",
        "/api/v1/auth/refresh",
        { refresh_token },
        { auth: false, skipRefresh: true },
      );
      setAuth(refreshed);
      return true;
    } catch (err) {
      if (err instanceof ApiError && [400, 401, 403].includes(err.status)) {
        clearAuth();
      }
      return false;
    }
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// Methods safe to retry on transient gateway hiccups (502/503/504). PATCH/POST/
// PUT/DELETE are intentionally excluded: most are not idempotent and a retry
// could double-apply a side effect.
const RETRY_SAFE_METHODS = new Set(["GET", "HEAD"]);
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

// Global 429 circuit breaker. When the gateway rate-limits us, idempotent
// GET/HEAD polls short-circuit for a short window instead of piling more
// requests onto an already throttled endpoint (several pollers run during a
// scan). Honors Retry-After; defaults to RATE_LIMIT_BACKOFF_MS. Writes
// (POST/PATCH/…) and the refresh call are never blocked.
const RATE_LIMIT_BACKOFF_MS = 5000;
let rateLimitedUntilMs = 0;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const auth = opts.auth !== false;
  const idempotent = RETRY_SAFE_METHODS.has(method.toUpperCase());

  // Interactive demo: answer data requests from the in-browser store instead of
  // the network. Auth, checkout and the jurisdiction catalog stay real.
  if (demoStore.active && inDemoScope(method, path)) {
    const res = handleDemoRequest(demoStore, method, path, body);
    if (res.status >= 400) {
      const detail =
        typeof (res.data as { detail?: unknown })?.detail === "string"
          ? (res.data as { detail: string }).detail
          : `Demo ${res.status}`;
      throw new ApiError(res.status, detail);
    }
    return res.data as T;
  }

  // Circuit breaker: while a 429 backoff window is open, idempotent polls fail
  // fast instead of adding more load to an already throttled gateway. The poll
  // ticks (and their .catch handlers) recover on the next interval once the
  // window passes.
  if (idempotent && Date.now() < rateLimitedUntilMs) {
    throw new ApiError(429, translate("errors.tooManyRequests"));
  }

  const send = () =>
    fetch(path, {
      method,
      headers: authHeaders(body !== undefined, auth),
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  let res: Response;
  try {
    res = await send();
  } catch (err) {
    // Network-level failure (DNS, refused, abort). One retry for idempotent
    // methods — covers the common case of a momentary gateway blip.
    if (RETRY_SAFE_METHODS.has(method.toUpperCase())) {
      await new Promise((r) => setTimeout(r, 600));
      res = await send();
    } else {
      throw err;
    }
  }
  if (res.status === 401 && auth && !opts.skipRefresh) {
    const refreshed = await refreshAuthSession();
    if (refreshed) res = await send();
  }
  // Transient gateway: one short retry for idempotent methods.
  if (TRANSIENT_STATUSES.has(res.status) && idempotent) {
    await new Promise((r) => setTimeout(r, 800));
    res = await send();
  }
  // Arm the global backoff whenever the gateway rate-limits us.
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get("retry-after"));
    const waitMs =
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : RATE_LIMIT_BACKOFF_MS;
    rateLimitedUntilMs = Date.now() + waitMs;
  }
  if (!res.ok) throw await parseApiError(res);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  return (ct.includes("application/json") ? await res.json() : await res.text()) as T;
}

// ---------- Attack vectors from the events feed ----------
//
// The backend removed `GET /scans/{id}/vectors`; attack-vector "hypotheses" are
// reconstructed from the scan events feed (`/scans/{id}/events`).
//
// Confirmed against a live scan (domain_pipline): this pipeline does NOT emit
// granular per-vector objects. The only event with structured per-object data
// is `finding.added` (`{lead_id, vuln_type, target, severity, status}`).
// Everything else is aggregate counts (`technique_surface.discovered.candidates`,
// `agentic.controller_plan.hot_leads`, `coverage.ledger`) or ships an empty
// `vector_id`/`family` (`agent.tool_executed`). So today this yields the
// confirmed findings only — it overlaps the Findings page. A real
// pending→tested→confirmed lifecycle needs the backend to emit per-lead events
// (requested); when it does, only this function changes and callers keep the
// `{ vectors: AttackVector[] }` contract. Events are folded oldest→newest so the
// latest status per vector wins.
type ScanEventsPage = {
  events: ScanEvent[];
  next_cursor: string;
  has_more: boolean;
};

function vectorStr(v: unknown): string | undefined {
  return typeof v === "string" && v ? v : undefined;
}

// An event describes a vector/hypothesis if it carries a NON-EMPTY lead/vector
// id, or (finding-shaped) a non-empty vuln_type on a host/target. Requiring
// non-empty values is essential: `agent.tool_executed` ships an empty
// `vector_id` key plus a multi-line `target` blob — matching on mere key
// presence flooded the list with junk "unknown/pending" entries.
function looksLikeVector(payload: Record<string, unknown>): boolean {
  if (!payload || typeof payload !== "object") return false;
  if (vectorStr(payload.vector_id) ?? vectorStr(payload.lead_id)) return true;
  return Boolean(
    (vectorStr(payload.vuln_type) ?? vectorStr(payload.vulnerability)) &&
      (vectorStr(payload.host) ?? vectorStr(payload.target)),
  );
}

export function deriveAttackVectorsFromEvents(
  events: ScanEvent[],
): AttackVector[] {
  const byKey = new Map<string, AttackVector>();
  for (const ev of events ?? []) {
    const payload = (ev?.payload ?? {}) as Record<string, unknown>;
    if (!looksLikeVector(payload)) continue;
    const id = vectorStr(payload.vector_id) ?? vectorStr(payload.lead_id);
    const host = vectorStr(payload.host) ?? vectorStr(payload.target) ?? "";
    const vulnType =
      vectorStr(payload.vuln_type) ?? vectorStr(payload.vulnerability) ?? "unknown";
    const key = id ?? `${vulnType}::${host}`;
    const prev = byKey.get(key);
    byKey.set(key, {
      vector_id: id ?? prev?.vector_id ?? key,
      scan_id: ev?.scan_id ?? prev?.scan_id ?? "",
      vuln_type: vulnType !== "unknown" ? vulnType : prev?.vuln_type ?? vulnType,
      target: vectorStr(payload.target) ?? prev?.target,
      host: host || prev?.host || "",
      severity: vectorStr(payload.severity) ?? prev?.severity,
      reason: vectorStr(payload.reason) ?? prev?.reason,
      how_to_test: vectorStr(payload.how_to_test) ?? prev?.how_to_test,
      status:
        (vectorStr(payload.status) as AttackVectorStatus) ??
        prev?.status ??
        "pending",
      created_at: prev?.created_at ?? ev?.timestamp ?? null,
    });
  }
  return [...byKey.values()];
}

// ---------- API surface ----------

export const api = {
  // ----- auth -----
  login(email: string, password: string) {
    return request<AuthResponse>(
      "POST",
      "/api/v1/auth/login",
      { email, password },
      { auth: false },
    );
  },
  register(body: { email: string; password: string; display_name: string }) {
    return request<AuthResponse>(
      "POST",
      "/api/v1/auth/register",
      body,
      { auth: false },
    );
  },
  me() {
    return request<{ user: User; auth_method: string }>("GET", "/api/v1/auth/me");
  },
  logout() {
    const refresh_token = getRefreshToken();
    if (!refresh_token) return Promise.resolve({ logged_out: true });
    return request<{ logged_out: boolean }>(
      "POST",
      "/api/v1/auth/logout",
      { refresh_token },
      { skipRefresh: true },
    );
  },
  refreshSession() {
    const refresh_token = getRefreshToken();
    if (!refresh_token) return Promise.reject(new ApiError(401, "No refresh token"));
    return request<AuthResponse>(
      "POST",
      "/api/v1/auth/refresh",
      { refresh_token },
      { auth: false, skipRefresh: true },
    );
  },
  changePassword(body: { current_password: string; new_password: string }) {
    // Backend rotates tokens on success — returns a fresh AuthSessionResponse.
    return request<AuthResponse>("POST", "/api/v1/auth/password/change", body);
  },
  startPasswordRecovery(email: string) {
    return request<{ accepted: boolean; cooldown_remaining_seconds: number }>(
      "POST",
      "/api/v1/auth/password/recovery/start",
      { email },
      { auth: false },
    );
  },
  completePasswordRecovery(body: { token: string; new_password: string }) {
    // Returns AuthSessionResponse — user is auto-logged in after reset.
    return request<AuthResponse>(
      "POST",
      "/api/v1/auth/password/recovery/complete",
      body,
      { auth: false },
    );
  },
  listApiKeys() {
    return request<{ api_keys: ApiKeyInfo[] }>("GET", "/api/v1/auth/api-keys");
  },
  createApiKey(body: ApiKeyCreateRequest) {
    return request<ApiKeyCreateResponse>("POST", "/api/v1/auth/api-keys", body);
  },
  revokeApiKey(id: string) {
    return request<ApiKeyInfo>(
      "DELETE",
      `/api/v1/auth/api-keys/${encodeURIComponent(id)}`,
    );
  },
  async ssoProviders(): Promise<{ providers: SsoProvider[] }> {
    // .v2: предыдущая версия не валидировала кеш и могла подсунуть HTML
    // (когда edge без /api/* proxy фолбэчил на index.html). Бамп ключа
    // гарантирует, что пользователи с битым кешем не упрутся в крах.
    // .v3: добавлен Яндекс — сбрасываем часовой кеш, чтобы кнопка появилась
    // сразу после включения провайдера на бэке.
    const CACHE_KEY = "ces.sso.providers.v3";
    const TTL_MS = 60 * 60 * 1000;
    const isValid = (
      x: unknown,
    ): x is { providers: SsoProvider[] } =>
      typeof x === "object" && x !== null &&
      Array.isArray((x as { providers?: unknown }).providers);
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw) as { ts: number; data: unknown };
        if (
          typeof cached?.ts === "number" &&
          Date.now() - cached.ts < TTL_MS &&
          isValid(cached.data)
        ) {
          return cached.data;
        }
      }
    } catch {
      // ignore parse errors, fall through to network
    }
    let data: unknown;
    try {
      data = await request<unknown>(
        "GET",
        "/api/v1/auth/sso/providers",
        undefined,
        { auth: false },
      );
    } catch {
      // network/edge ошибка — рисуем UI без SSO, лучше чем белый экран.
      return { providers: [] };
    }
    const safe: { providers: SsoProvider[] } = isValid(data)
      ? data
      : { providers: [] };
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ ts: Date.now(), data: safe }),
      );
    } catch {
      // quota or disabled storage — fine, just skip cache
    }
    return safe;
  },
  startSso(providerKey: string) {
    // Pass our /auth/callback URL as ``continue_url`` so the auth-service
    // lands the OAuth callback back on the same host the user started
    // from (also keeps localhost/preview logins working) — without it the
    // backend's single configured ``oauth_frontend_callback_url`` decides
    // where users end up regardless of where they signed in from.
    const continueUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}/auth/callback`
        : "";
    const qs = continueUrl
      ? `?continue_url=${encodeURIComponent(continueUrl)}`
      : "";
    return request<SsoStartResponse>(
      "GET",
      `/api/v1/auth/sso/start/${encodeURIComponent(providerKey)}${qs}`,
      undefined,
      { auth: false },
    );
  },
  listIdentities() {
    return request<{ identities: Array<Record<string, unknown>> }>(
      "GET",
      "/api/v1/auth/identities",
    );
  },
  unlinkIdentity(id: string) {
    return request<DeleteResponse>(
      "DELETE",
      `/api/v1/auth/identities/${encodeURIComponent(id)}`,
    );
  },
  startEmailVerification() {
    return request<{ accepted: boolean; cooldown_remaining_seconds: number }>(
      "POST",
      "/api/v1/auth/email/verification/start",
      {},
    );
  },
  completeEmailVerification(token: string) {
    return request<{ verified: boolean; already_verified: boolean }>(
      "POST",
      "/api/v1/auth/email/verification/complete",
      { token },
      { auth: false },
    );
  },

  // ----- projects -----
  listProjects(params: CursorParams = {}) {
    return request<Cursored<"projects", Project>>(
      "GET",
      `/api/v1/projects${qs(params)}`,
    );
  },
  getProject(id: string) {
    return request<Project>("GET", `/api/v1/projects/${id}`);
  },
  createProject(body: { name: string; description?: string; jurisdiction_code?: string }) {
    return request<Project>("POST", "/api/v1/projects", body);
  },
  updateProject(
    id: string,
    body: {
      name?: string | null;
      description?: string | null;
      jurisdiction_code?: string;
    },
  ) {
    return request<Project>("PATCH", `/api/v1/projects/${id}`, body);
  },
  deleteProject(id: string) {
    return request<DeleteResponse>("DELETE", `/api/v1/projects/${id}`);
  },
  // Reference catalog for the jurisdiction select. Returns only enabled entries;
  // a plain user Bearer is enough (no admin). A failure here must not block
  // project creation — callers fall back to omitting the code.
  listJurisdictions() {
    return request<{ jurisdictions: Jurisdiction[] }>("GET", "/api/v1/jurisdictions");
  },

  // ----- scope / targets -----
  listScopeTargets(projectId: string) {
    return request<{ targets: ScopeTarget[] }>(
      "GET",
      `/api/v1/projects/${projectId}/scope/targets`,
    );
  },
  addScopeTarget(projectId: string, target: string) {
    return request<ScopeTarget>(
      "POST",
      `/api/v1/projects/${projectId}/scope/targets`,
      { target },
    );
  },
  getScopeTarget(targetId: string) {
    return request<ScopeTarget>("GET", `/api/v1/scope/targets/${targetId}`);
  },
  deleteScopeTarget(targetId: string) {
    return request<DeleteResponse>("DELETE", `/api/v1/scope/targets/${targetId}`);
  },
  startVerifyTarget(
    targetId: string,
    method: "dns_txt" | "file" | "meta" | "dns" = "dns",
  ) {
    return request<ScopeTarget>(
      "POST",
      `/api/v1/scope/targets/${targetId}/verification/start`,
      { method },
    );
  },
  completeVerifyTarget(targetId: string) {
    return request<VerificationCompleteResponse>(
      "POST",
      `/api/v1/scope/targets/${targetId}/verification/complete`,
      {},
    );
  },

  // ----- scans -----
  startScan(
    target_id: string,
    opts: {
      pipeline_name?: string | null;
      auth_profile_id?: string | null;
      operator_brief?: string | null;
    } = {},
  ) {
    return request<StartScanResponse>("POST", "/api/v1/scans", { target_id, ...opts });
  },
  listScans(input: string | ListScansParams = {}) {
    const params: ListScansParams =
      typeof input === "string" ? { project_id: input } : input;
    return request<Cursored<"scans", ScanListItem>>(
      "GET",
      `/api/v1/scans${qs(params)}`,
    );
  },
  getScan(id: string) {
    return request<Scan>("GET", `/api/v1/scans/${id}`);
  },
  pauseScan(id: string) {
    return request<Scan>("POST", `/api/v1/scans/${id}/pause`, {});
  },
  resumeScan(id: string) {
    return request<Scan>("POST", `/api/v1/scans/${id}/resume`, {});
  },
  cancelScan(id: string) {
    return request<Scan>("POST", `/api/v1/scans/${id}/cancel`, {});
  },
  scanFindings(id: string) {
    return request<{
      scan_id: string;
      findings: Finding[];
      total: number;
    }>("GET", `/api/v1/scans/${id}/findings`);
  },
  /**
   * Pipeline DAG скана с live-статусами per node + recursive children.
   * Mission Engine эмитит `mission.iter_NN` и `mission.iter_NN.tool_MM_*`
   * как child node_runs — этот endpoint возвращает их как nested tree
   * для `<MissionGraph>` render'а.
   */
  scanGraph(id: string) {
    return request<{
      scan_id: string;
      pipeline_name: string;
      pipeline_version: string;
      nodes: ScanGraphNode[];
      edges: Array<{ from: string; to: string; kind: string }>;
    }>("GET", `/api/v1/scans/${id}/graph`);
  },
  getFinding(scanId: string, findingId: string) {
    return request<Finding>(
      "GET",
      `/api/v1/scans/${scanId}/findings/${findingId}`,
    );
  },
  scanEvents(id: string, input: string | ScanEventsParams = {}) {
    const params: ScanEventsParams =
      typeof input === "string" ? { cursor: input } : input;
    return request<{
      scan_id: string;
      cursor: string;
      next_cursor: string;
      events: ScanEvent[];
      count: number;
      has_more: boolean;
    }>("GET", `/api/v1/scans/${id}/events${qs(params)}`);
  },
  scanEventsStreamUrl(id: string): string {
    return `/api/v1/scans/${id}/events/stream`;
  },
  // Punch-up raw scan events into narration bubbles via the Worker proxy.
  // Absolute cross-origin call (NOT through `request`): no gateway, no Bearer.
  // Throws if `VITE_NARRATE_URL` is unset — callers must guard with
  // `isNarrationEnabled()` and fall back to the local template layer.
  async narrate(
    items: NarrateItem[],
    lang: string = "ru",
    signal?: AbortSignal,
  ): Promise<{ results: NarratedBubble[] }> {
    // Demo: no worker call — the chat renders its deterministic template layer.
    if (demoStore.active) return { results: [] };
    if (!NARRATE_URL) throw new Error("narration_disabled");
    if (items.length === 0) return { results: [] };
    const res = await fetch(NARRATE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items, lang }),
      signal,
    });
    if (!res.ok) throw new ApiError(res.status, `narrate_failed_${res.status}`);
    return (await res.json()) as { results: NarratedBubble[] };
  },
  listScanArtifacts(scanId: string) {
    return request<{ artifacts: Array<Record<string, unknown>> }>(
      "GET",
      `/api/v1/scans/${scanId}/artifacts`,
    );
  },
  scanArtifactDownloadUrl(scanId: string, artifactId: string) {
    return `/api/v1/scans/${scanId}/artifacts/${encodeURIComponent(artifactId)}/download`;
  },
  listHostTranscripts(scanId: string) {
    return request<{ hosts: Array<Record<string, unknown>> }>(
      "GET",
      `/api/v1/scans/${scanId}/transcripts`,
    );
  },
  getHostTranscript(scanId: string, host: string) {
    return request<string>(
      "GET",
      `/api/v1/scans/${scanId}/transcripts/${encodeURIComponent(host)}`,
    );
  },
  // `/scans/{id}/vectors` was removed from the backend. Vectors are now emitted
  // into the scan events feed, so we page through events (oldest→newest, so the
  // latest status per vector wins) and reconstruct them client-side. The return
  // shape stays `{ vectors }` so callers (Hypotheses / Overview) are unchanged.
  async listAttackVectors(scanId: string): Promise<{ vectors: AttackVector[] }> {
    const events: ScanEvent[] = [];
    let cursor: string | null = null;
    // Bound paging so a very long scan can't spin forever.
    for (let page = 0; page < 25; page++) {
      const res: ScanEventsPage = await request(
        "GET",
        `/api/v1/scans/${scanId}/events${qs({ cursor, page_size: 200 })}`,
      );
      events.push(...(res.events ?? []));
      if (!res.has_more || !res.next_cursor) break;
      cursor = res.next_cursor;
    }
    return { vectors: deriveAttackVectorsFromEvents(events) };
  },

  // ----- reports (per-scan) -----
  // The server assembles the file from the currently admitted finding set on
  // every call; this is not a link to a previously published PDF. Owner
  // download takes only `format` — locale/brand/prompt_key are admin-only.
  // Replies 409 report_not_ready until the scan reaches a terminal status.
  scanReportUrl(scanId: string, format: "json" | "sarif" | "html" | "pdf" = "pdf"): string {
    return `/api/v1/scans/${scanId}/report?format=${encodeURIComponent(format)}`;
  },
  // Manual attestation state of the report composition. Allowed before the
  // scan completes (answers `not_completed`); served with Cache-Control: no-store.
  getReportVerification(scanId: string) {
    return request<ReportVerificationInfo>(
      "GET",
      `/api/v1/scans/${scanId}/report/verification`,
    );
  },

  // ----- notifications -----
  listNotifications(input: boolean | ListNotificationsParams = {}) {
    const params: ListNotificationsParams =
      typeof input === "boolean" ? { only_unread: input } : input;
    return request<Cursored<"notifications", Notification>>(
      "GET",
      `/api/v1/notifications${qs(params)}`,
    );
  },
  markRead(id: string) {
    return request<{ updated: boolean }>(
      "POST",
      `/api/v1/notifications/${id}/read`,
      {},
    );
  },
  notificationPrefs() {
    return request<NotificationPrefs>("GET", "/api/v1/notifications/preferences");
  },
  updateNotificationPrefs(body: {
    event_preferences?: Record<string, NotificationChannel[]>;
    global_mute?: boolean;
    notification_email?: string;
  }) {
    return request<NotificationPrefs>(
      "POST",
      "/api/v1/notifications/preferences",
      body,
    );
  },
  listNotificationDeliveries(params: CursorParams = {}) {
    return request<Cursored<"deliveries", Record<string, unknown>>>(
      "GET",
      `/api/v1/notifications/deliveries${qs(params)}`,
    );
  },
  listWebhooks() {
    return request<{ webhooks: Array<Record<string, unknown>> }>(
      "GET",
      "/api/v1/notifications/webhooks",
    );
  },
  registerWebhook(body: { url: string; events?: string[]; secret?: string }) {
    return request<Record<string, unknown>>(
      "POST",
      "/api/v1/notifications/webhooks",
      body,
    );
  },
  deleteWebhook(id: string) {
    return request<DeleteResponse>(
      "DELETE",
      `/api/v1/notifications/webhooks/${encodeURIComponent(id)}`,
    );
  },
  listPushDevices() {
    return request<{ devices: Array<Record<string, unknown>> }>(
      "GET",
      "/api/v1/notifications/push/devices",
    );
  },
  registerPushDevice(body: { platform: string; token: string }) {
    return request<Record<string, unknown>>(
      "POST",
      "/api/v1/notifications/push/devices",
      body,
    );
  },
  deletePushDevice(id: string) {
    return request<DeleteResponse>(
      "DELETE",
      `/api/v1/notifications/push/devices/${encodeURIComponent(id)}`,
    );
  },

  // ----- billing -----
  subscription() {
    return request<SubscriptionInfo>("GET", "/api/v1/billing/subscription");
  },
  cancelSubscription() {
    return request<{ cancelled: boolean; subscription: BillingSubscriptionDetails }>(
      "POST",
      "/api/v1/billing/subscription/cancel",
      {},
    );
  },
  billingPlans(params: CursorParams = {}) {
    return request<Cursored<"plans", BillingPlan>>(
      "GET",
      `/api/v1/billing/plans${qs(params)}`,
    );
  },
  checkout(args: {
    plan_tier: string;
    success_url: string;
    cancel_url: string;
    provider?: string;
    customer_email?: string;
    metadata?: Record<string, string>;
  }) {
    return request<CheckoutResponse>("POST", "/api/v1/billing/checkout", args);
  },
  scanPackPricing() {
    return request<BillingScanPackPricing>(
      "GET",
      "/api/v1/billing/scan-packs/pricing",
    );
  },
  scanPackCheckout(args: {
    scans_count: number;
    success_url: string;
    cancel_url: string;
    provider?: string;
    customer_email?: string;
    metadata?: Record<string, string>;
  }) {
    return request<CheckoutResponse>(
      "POST",
      "/api/v1/billing/scan-packs/checkout",
      args,
    );
  },
  listBillingInvoices(params: CursorParams = {}) {
    return request<Cursored<"invoices", BillingInvoice>>(
      "GET",
      `/api/v1/billing/invoices${qs(params)}`,
    );
  },
  invoiceDownloadUrl(id: string) {
    return `/api/v1/billing/invoices/${encodeURIComponent(id)}/download`;
  },
};

// ---------- Helpers ----------

export function isProjectVerified(p: Project): boolean {
  const total = p.total_scope_targets ?? 0;
  const verified = p.verified_targets ?? 0;
  return total > 0 && verified >= total;
}

/** Verification state of a single target. Unlike isProjectVerified this reads
 * the target's own status instead of the project's counters, which the backend
 * updates asynchronously. */
export function isTargetVerified(t: ScopeTarget): boolean {
  return t.verification_status === "verified" || Boolean(t.verified_at);
}

/** Force a project's counters to "fully verified" so isProjectVerified() agrees
 * with a verification we watched succeed at target level. Used while the
 * backend's own counters catch up — see AppStateProvider.locallyVerified. */
export function asVerifiedProject(p: Project): Project {
  // total can still be 0 mid-catch-up, and isProjectVerified requires total > 0.
  const total = Math.max(p.total_scope_targets ?? 0, 1);
  return { ...p, total_scope_targets: total, verified_targets: total };
}

export function normalizeApiText(value: string | null | undefined): string {
  return (value ?? "unknown").toLowerCase();
}

export function normalizeScanStatus(status: string | null | undefined): string {
  return normalizeApiText(status);
}

export function isActiveScanStatus(status: string | null | undefined): boolean {
  const normalized = normalizeScanStatus(status);
  return normalized === "running" || normalized === "paused";
}

export function subscriptionLimits(
  subscription: SubscriptionInfo | null | undefined,
): BillingPlan | null {
  return subscription?.plan ?? null;
}

/** Clamp/round a percentage value to 0..100. Accepts either a 0..1 fraction
 * (legacy) or a 0..100 percent (new spec) and normalises to the latter. */
export function progressPercent(progress: number | undefined | null): number {
  if (progress == null || !Number.isFinite(progress)) return 0;
  const normalized = progress <= 1 ? progress * 100 : progress;
  return Math.max(0, Math.min(100, Math.round(normalized)));
}

const STAGE_NAMES = ["RECON", "MAP", "ANALYZE", "EXPLOIT", "CHAIN", "REPORT"] as const;
const STAGE_KEYS = ["recon", "map", "analyze", "exploit", "chain", "report"] as const;

const TERMINAL_NODE_STATUSES = new Set([
  "completed",
  "succeeded",
  "success",
  "failed",
  "error",
  "skipped",
  "cancelled",
  "canceled",
]);

function flattenGraphNodes(nodes: ScanGraphNode[]): ScanGraphNode[] {
  const out: ScanGraphNode[] = [];
  const stack = [...nodes];
  while (stack.length) {
    const n = stack.pop()!;
    out.push(n);
    if (n.children?.length) stack.push(...n.children);
  }
  return out;
}

function stageKeyOf(nodeId: string | null | undefined): string | null {
  if (!nodeId) return null;
  const prefix = nodeId.toLowerCase().split(".")[0];
  return STAGE_KEYS.includes(prefix as (typeof STAGE_KEYS)[number]) ? prefix : null;
}

/** Map a pipeline node to one of the six display stages. Simple pipelines use
 * explicit `recon.subfinder` / `map.*` id prefixes (handled by stageKeyOf). The
 * mission_web_engagement family uses a flat node taxonomy (`prefetch::gau`,
 * `kb_planner_*`, `mission.iter.*`, `llm_*`, …), so we classify by id/type
 * keywords. Order matters: it follows execution order so the bars fill
 * sequentially — EXPLOIT is reserved for the mission loop itself, not the
 * exploit-flavoured tools that get *seeded* during the earlier MAP phase.
 * Returns null for orchestration/glue nodes that shouldn't count toward a bar. */
function classifyStage(node: ScanGraphNode): string | null {
  const legacy = stageKeyOf(node.id);
  if (legacy) return legacy;
  const id = (node.id ?? "").toLowerCase();
  const type = (node.type ?? "").toLowerCase();
  const s = `${id} ${type}`;
  if (/remediation|lesson|release\b|executive|(^|_)report(_|$)/.test(s)) return "report";
  if (/replay|post_mission|post_audit|post_success|verified_audit|verified_findings|recalibrat|finalize|resolve_pending/.test(s)) return "chain";
  // ANALYZE before EXPLOIT so `llm_mission_brief` / coverage nodes don't fall
  // into the mission bucket on a stray "mission"/"vector" keyword.
  if (/^llm_|llm\.complete|(^|_)kb[_.]|(^|_)rag(_|\.|$)|hypothesis|strateg|specialist|triage|coverage|surface_inventory|vectorize|guardrail|context_|chain_planner|mission_brief|structured_action|first_findings|payload_matrix/.test(s)) return "analyze";
  if (/^mission/.test(id) || type.startsWith("mission.")) return "exploit";
  if (/probe|wave|surface|ffuf|cors|admin|robots|sitemap|js_|param|host|auth_|api_|graphql|storage|supabase|secret|form|redirect|http_method|path_normal|nikto|framework|sensitive|browser|crawl|positive_signal|abuse|nmap_web|nmap_script|legacy_web|exposure|sqlmap|dalfox|commix|tplmap|injection|idor|bola|mass_assignment|seed|verify|ingest/.test(s)) return "map";
  if (/provision|prefetch|tech|cve|dns|http|subfinder|gau|wayback|nmap|recon|live_|target|waf|webanalyze/.test(s)) return "recon";
  return null;
}

/** Per-stage progress derived from the live pipeline graph — counts each
 * node's terminal status to report real completion (e.g. `RECON: 4/7`),
 * not an approximation of the aggregate progress_percent. */
export function deriveScanStagesFromGraph(graph: {
  nodes: ScanGraphNode[];
}): Array<{ name: string; pct: number; done: number; total: number }> {
  const all = flattenGraphNodes(graph.nodes);
  const buckets = new Map<string, { done: number; total: number }>();
  for (const key of STAGE_KEYS) buckets.set(key, { done: 0, total: 0 });
  for (const node of all) {
    const key = classifyStage(node);
    if (!key) continue;
    const b = buckets.get(key)!;
    b.total += 1;
    if (TERMINAL_NODE_STATUSES.has((node.status ?? "").toLowerCase())) {
      b.done += 1;
    }
  }
  return STAGE_NAMES.map((name, i) => {
    const b = buckets.get(STAGE_KEYS[i])!;
    const pct = b.total === 0 ? 0 : Math.round((b.done / b.total) * 100);
    return { name, pct, done: b.done, total: b.total };
  });
}

/** Stage progress for the ScanBar. Anchor the active stage off the engine's
 * `current_node_id` (e.g. `recon.subfinder` → RECON) so finished stages snap
 * to 100% the moment the engine moves on, instead of waiting for the bulk
 * `progress_percent` to drift past an arbitrary 1/6 threshold (which was
 * unreliable: stages don't have equal node counts, so e.g. a small RECON
 * could show as half-done while actually complete). Falls back to the even
 * split when current_node_id is absent or unknown. */
export function deriveScanStages(
  scan: Pick<Scan, "progress_percent" | "nodes_done" | "nodes_total" | "current_node_id" | "status"> & { phase?: string | null },
) {
  const pct = progressPercent(scan.progress_percent ?? null);
  const status = normalizeScanStatus(scan.status);
  const stageWidth = 100 / STAGE_NAMES.length;

  // Only trust the explicit status. progress_percent hits 100 long before
  // a mission scan is actually done (orchestration nodes complete instantly,
  // real work happens inside mission iterations) — falling back to "all
  // 100%" on pct>=100 would re-introduce the premature-completion bug.
  if (status === "completed") {
    return STAGE_NAMES.map((name) => ({ name, pct: 100 }));
  }

  const activeIdx = STAGE_KEYS.indexOf(
    (stageKeyOf(scan.current_node_id) ?? "") as (typeof STAGE_KEYS)[number],
  );

  if (activeIdx >= 0) {
    return STAGE_NAMES.map((name, i) => {
      if (i < activeIdx) return { name, pct: 100 };
      if (i > activeIdx) return { name, pct: 0 };
      // Approximate progress within the active stage using whatever overall
      // pct we have. Backend doesn't expose per-stage counts; this is the
      // best honest estimate without lying about completion.
      const start = activeIdx * stageWidth;
      const within = Math.max(0, Math.min(stageWidth, pct - start));
      return { name, pct: Math.round((within / stageWidth) * 100) };
    });
  }

  // current_node_id exists but doesn't map to our RECON/MAP/.../REPORT
  // taxonomy — typical for mission-driven pipelines (`mission.iter_NN.*`).
  // We can't truthfully attribute progress to a specific stage, but a scan
  // that's mid-iteration with findings shouldn't show all bars at 0% either.
  // Show the aggregate `progress_percent` evenly across all bars so they
  // move together — the bars lose their phase meaning, but at least reflect
  // "the scan is making progress". The 99% clamp in getScanStages then
  // prevents them from claiming completion while the scan is still running.
  if ((scan.current_node_id ?? "").trim() !== "") {
    return STAGE_NAMES.map((name) => ({ name, pct }));
  }

  return STAGE_NAMES.map((name, i) => {
    const start = i * stageWidth;
    const end = (i + 1) * stageWidth;
    const local = pct <= start ? 0 : pct >= end ? 100 : Math.round(((pct - start) / stageWidth) * 100);
    return { name, pct: local };
  });
}

/** Combined helper — prefers the graph-derived (honest) stages when the
 * graph carries nodes with our stage prefixes, else falls back to the
 * scan-derived approximation. A completed scan always reports all 100%
 * regardless of source, because the graph for some pipelines (e.g.
 * mission_web_engagement) uses ids like `mission.iter_NN.*` that don't
 * map to the RECON/MAP/... taxonomy. */
export function getScanStages(
  scan:
    | (Pick<Scan, "progress_percent" | "nodes_done" | "nodes_total" | "current_node_id" | "status"> & { scan_id?: string })
    | null
    | undefined,
  graph: { scan_id?: string; nodes: ScanGraphNode[] } | null | undefined,
): Array<{ name: string; pct: number; done?: number; total?: number }> {
  if (!scan) return STAGE_NAMES.map((name) => ({ name, pct: 0 }));
  const status = normalizeScanStatus(scan.status);
  if (status === "completed") {
    return STAGE_NAMES.map((name) => ({ name, pct: 100 }));
  }
  // Skip the graph when it belongs to a different scan than what we're
  // rendering (e.g. user is viewing a historical scan while the active one
  // is something else). Without this guard we'd briefly show the active
  // scan's stages under the viewed scan's bar.
  const graphMatches = !!graph && graph.nodes.length > 0 && (!graph.scan_id || !scan.scan_id || graph.scan_id === scan.scan_id);
  let stages: Array<{ name: string; pct: number; done?: number; total?: number }>;
  if (graphMatches) {
    const fromGraph = deriveScanStagesFromGraph(graph!);
    if (fromGraph.some((s) => (s.total ?? 0) > 0)) {
      stages = fromGraph;
    } else {
      stages = deriveScanStages(scan);
    }
  } else {
    stages = deriveScanStages(scan);
  }
  // While the overall scan is still running, never let a stage display 100%.
  // The orchestration pipeline can complete every step (so the graph counts
  // them all as "done") while the report is still being compiled and the
  // backend keeps the scan in `running`. Pinning to 99 keeps the visual
  // "still working" signal honest until status flips to `completed`.
  if (status === "running" || status === "paused") {
    stages = stages.map((s) => (s.pct >= 100 ? { ...s, pct: 99 } : s));
  }
  return stages;
}

export async function downloadAuthenticated(url: string, filename: string): Promise<void> {
  // Demo: the report is built in the browser from the sample findings and
  // handed over as a self-contained HTML file (host-accurate, no server call).
  const demoReport = demoReportMatch(url);
  if (demoReport) {
    const host = demoStore.getState().host ?? "acme-store.example";
    const blob = new Blob([await demoReportHtml(host)], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename.replace(/\.(pdf|sarif|json|html)$/i, "") + ".html";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
    return;
  }
  const send = () =>
    fetch(url, {
      headers: authHeaders(false, true),
    });
  let res = await send();
  if (res.status === 401 && (await refreshAuthSession())) {
    res = await send();
  }
  if (!res.ok) throw await parseApiError(res);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
}

export async function fetchAuthenticatedText(url: string): Promise<string> {
  // Demo: the report preview reads the in-browser HTML (no server call). A
  // short delay lets the preview iframe mount with its loading content first,
  // so swapping in the report reliably reloads it (an instant swap doesn't).
  if (demoReportMatch(url)) {
    await new Promise((r) => setTimeout(r, 150));
    return await demoReportHtml(demoStore.getState().host ?? "acme-store.example");
  }
  const send = () =>
    fetch(url, {
      headers: authHeaders(false, true),
    });
  let res = await send();
  if (res.status === 401 && (await refreshAuthSession())) {
    res = await send();
  }
  if (!res.ok) throw await parseApiError(res);
  return res.text();
}
